<template>
  <div>
        <span title="More information" v-on:click="aboutPage = true" class="about">
            <font-awesome-icon icon="info"/>
        </span>
    <transition name="fade">
      <div v-if="aboutPage" v-on:click.self="aboutPage = false" class="aboutPage">
        <div class="aboutPage-content">
          <span v-on:click="aboutPage = false" style="float: right"> <font-awesome-icon icon="times"/></span>
          <h1>Laborshed in Southeast Michigan - Linking Residents to Workplaces</h1>
          <div style="float: right; padding-left: 5px;">
            <iframe style=" max-width: 100%;" width="560" height="315" src="https://www.youtube.com/embed/gX9D1B7dhc8"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen></iframe>
          </div>
          <p>This mapping application shows where Southeast Michigan’s workers live, work, and their commutes between
            both locations. It also provides an estimate of nonworking residents – or people who are either not employed
            or looking for work. The data are structured by census tract.</p>
          <p>The intention of this application is to help connect workers to employers that need their talent. This
            map can help answer questions for many stakeholders. Examples include: </p>
          <ul>
            <li>In what neighborhoods can I find production workers for a new manufacturing facility?</li>
            <li>Do workers in a particular industry (e.g., retail) live near their workplaces?</li>
            <li>Where do workers in my community (or census tract) commute from?</li>
            <li>I would like to recruit workers for apprenticeships right out of high school. What tracts have a higher
              density of nonworking residents, age 16-24?
            </li>
            <li>I would like to get more people engaged in the labor force. Which neighborhoods currently have higher
              densities of nonworking residents?
            </li>
          </ul>

          <p>There are three layers to choose from: 1) workers by residence, 2) workers by workplace, and 3) nonworking
            residents. These layers are defined as:</p>

          <ul>
            <li><strong>Workers by Residence</strong> – where workers live</li>
            <li><strong>Workers by Workplace</strong> – where workers work</li>
            <li><strong>Nonworking Residents</strong> – people who are not in the labor force, meaning they are not
              working and have
              not looked for a job.
            </li>
          </ul>

          <h2>Workers by Residence and Workplace</h2>
          <p>Workers are defined as people, age 16 and older, with a job. If a worker holds more than one job, only
            the job with the greatest number of hours worked is recorded (this is also known as the primary job). The
            map shows where workers live and where workers work. Density is calculated using workers per acre. Workers
            are categorized by industry or occupation they work in.</p>

          <div style="margin-left: 40px">
            <h4>Industry or Occupation</h4>
            <p>The layers for workers by residence and workplace can be filtered by industry
              or occupation. There are 14
              industry sectors and 24 occupational groups. Industries are based on the 2012 <a
                  href="https://www.census.gov/naics/" target="_blank">American Industry
                Classification System (NAICS)</a>. Occupations are based on the 2010 <a
                  href="https://www.bls.gov/soc/" target="_blank">Standard Occupational
                Classification
                (SOC)</a>.</p>

            <h4>Commuter Flows</h4>
            <p>The application also shows commuting or worker flows from residence to
              workplace (and workplace to
              residence). When Commuter Flow is selected, the application automatically selects a default census tract
              (outlined in red). Drag the marker to the tract that you would like to analyze. The circular symbols
              show
              where workers in that tract commute to/from. </p>

            <p>To analyze where workers commute to, select Outflow from the dropdown box and the circular symbols show
              the tracts that are workplace destinations. Each circle shows the estimated number of commuters to each
              destination tract. </p>

            <p>To analyze where workers commute from, select Inflow from the dropdown box and the circular symbols
              show
              the tracts that are residence origins. Each circle shows the estimated number of commuters from each
              origin tract.</p>

            <p>Flow data can be filtered by 7 industry sectors, which are the same sectors as those found in the
              residence/workplace layers but aggregated into broader groups. The following table shows how they are
              related: </p>

            <h4>Industry Sector Equivalencies</h4>

            <table>
              <tr>
                <th style="text-align: center;">Code</th>
                <th style="text-align: start;">Categories for Residence and Workplace
                </th>
                <th style="text-align: center;">Code</th>
                <th style="text-align: start;">Categories for Commuter Flows
                </th>
              </tr>
              <tr>
                <th>99</th>
                <td> Total</td>
                <th>1</th>
                <td> Total</td>
              </tr>
              <tr>
                <th>11</th>
                <td> Agriculture, forestry, fishing and hunting, and mining</td>
                <th>2</th>
                <td> Natural Resources, Mining, Construction, & Armed Forces</td>
              </tr>
              <tr>
                <th>95</th>
                <td>Armed Forces
                </td>
                <th>2</th>
                <td>Natural Resources, Mining, Construction, & Armed Forces
                </td>
              </tr>
              <tr>
                <th>71</th>
                <td>Arts, entertainment, recreation, accommodation and food services
                </td>
                <th>7</th>
                <td>Leisure and Hospitality
                </td>
              </tr>
              <tr>
                <th>23</th>
                <td>Construction
                </td>
                <th>2</th>
                <td> Natural Resources, Mining, Construction, & Armed Forces
                </td>
              </tr>
              <tr>
                <th>65</th>
                <td>Educational, health and social services
                </td>
                <th>6</th>
                <td>Education and Health Services
                </td>
              </tr>
              <tr>
                <th>55</th>
                <td>Finance, insurance, real estate and rental and leasing
                </td>
                <th>5</th>
                <td>Information, Financial Activities, & Professional and Business Services
                </td>
              </tr>
              <tr>
                <th>50</th>
                <td>Information
                </td>
                <th>5</th>
                <td>Information, Financial Activities, & Professional and Business Services
                </td>
              </tr>
              <tr>
                <th>30</th>
                <td>Manufacturing
                </td>
                <th>3</th>
                <td> Manufacturing
                </td>
              </tr>
              <tr>
                <th>80</th>
                <td>Other services (except public administration)
                </td>
                <th>8</th>
                <td>Other Services & Public Administration
                </td>
              </tr>
              <tr>
                <th>60</th>
                <td>Professional, scientific, management, administrative, and waste management services
                </td>
                <th>5</th>
                <td>Information, Financial Activities, & Professional and Business Services
                </td>
              </tr>
              <tr>
                <th>92</th>
                <td>Public administration
                </td>
                <th>8</th>
                <td>Other Services & Public Administration
                </td>
              </tr>
              <tr>
                <th>42</th>
                <td>Retail trade
                </td>
                <th>4</th>
                <td>Trade, Transportation, and Utilities
                </td>
              </tr>
              <tr>
                <th>43</th>
                <td>Transportation and warehousing, and utilities
                </td>
                <th>4</th>
                <td>Trade, Transportation, and Utilities
                </td>
              </tr>
              <tr>
                <th>41</th>
                <td>Wholesale trade
                </td>
                <th>4</th>
                <td>Trade, Transportation, and Utilities
                </td>
              </tr>
            </table>
            <div>Note that there are no occupation categories for the commuter flows.</div>
          </div>

          <h2>Nonworking Residents</h2>
          <p>Nonworking residents are defined as people, age 16 and older, who do not have a job, nor have they
            looked for one. There may be many reasons for not working – age, full-time school, caring for dependents,
            and lack of skills are some of the more common ones.</p>

          <p>Within this population is a subset of <strong style="font-style: italic;">potential</strong> workers, where, if they had the right support (e.g.,
            education, childcare, and/or transportation options), could become productive members of the labor
            force.</p>

          <p>Nonworking residents can be filtered by the following age groups:</p>
          <ul>
            <li>16 to 24</li>
            <li>25 to 64</li>
            <li>65 and up</li>
          </ul>

          <h2>Map Notes</h2>
          <p>The data are organized by census tract, but can be filtered by county or community. Census tracts nest
            within counties but may overlap communities; however, we assigned census tracts to communities based on
            majority area.</p>
          <p>Community queries include all census tracts that cross community boundaries.</p>
          <p>For more information on commuting patterns, please see SEMCOG’s <a
              href="https://maps.semcog.org/CommutingPatterns/" target="_blank">Commuting Patterns</a> map and
            <a href="https://semcog.org/desktopmodules/SEMCOG.Publications/GetFile.ashx?filename=QuickFactsCommutingInSoutheastMichigan20062010May2013.pdf"
               target="_blank">Commuting
              in
              Southeast Michigan, 2006-2010 report</a>. </p>
          <h2>Sources</h2>
          <div>
            <ul>
              <li><a href="https://www.census.gov/programs-surveys/acs" target="_blank">Census Bureau's American
                Community Survey (ACS) 2015-2019 5-Year Estimates</a> – for nonworking residents
              </li>
              <li><a href="https://ctpp.transportation.org/" target="_blank">Census Transportation Planning Products
                Program (CTPP) 2012-2016 5-Year CTPP</a> – for workers by residence, workplace, and flows
              </li>
            </ul>
          </div>
          <h2>For Questions, please contact:</h2>
          <div>
            <p>Data: <a href="mailto:parthum@semcog.org">Brian Parthum</a> or <a href="mailto:uppalapati@semcog.org">Sirisha
              Uppalapati</a></p>
            <p>Application: <a href="mailto:misiuk@semcog.org">Chad Misiuk</a></p>
          </div>
        </div>
      </div>
    </transition>

  </div>

</template>

<script>
export default {
  name: "AboutPage",
  data: function () {
    return {
      aboutPage: !this.$route.query.mcd,
    }
  },
}
</script>

<style scoped>

.about {
  float: right;
  padding: 5px;
}

.aboutPage {
  position: fixed;
  background: rgba(46, 60, 67, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  overflow: hidden;
}

.aboutPage-content {
  background: white;
  max-width: 80%;
  border: #fffcfc solid 2px;
  border-radius: 10px;
  padding: 10px 30px;
  max-height: 80%;
  overflow: auto;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th {
  text-align: center;
  border-bottom: 2px #64983D solid;
  padding-right: 25px;
}

td {
  border-bottom: 2px #64983D solid;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-enter-active .aboutPage-content, .fade-leave-active .aboutPage-content {
  transition: transform 1s;
}

.fade-enter .aboutPage-content, .fade-leave-to .aboutPage-content {
  transform: translate(-50vw, -50vh) scale(0);
}

.fade-enter-to .aboutPage-content, .fade-leave-to .aboutPage-content {
  overflow: hidden;
}

</style>
